import React, { useState, useEffect } from 'react';
import { Brain, TrendingUp, AlertTriangle, CheckCircle, RefreshCw } from 'lucide-react';
import { Contact, Activity, ContactSummary } from '../types';
import { aiService } from '../services/aiService';

interface ContactSummaryAIProps {
  contact: Contact;
  activities: Activity[];
}

const ContactSummaryAI: React.FC<ContactSummaryAIProps> = ({ contact, activities }) => {
  const [summary, setSummary] = useState<ContactSummary | null>(null);
  const [loading, setLoading] = useState(false);

  // Rate limiting is now enforced server-side by openai-proxy. The client
  // just calls and handles the 429 response if the user hits the limit.

  const generateSummary = async () => {
    setLoading(true);
    try {
      const aiSummary = await aiService.generateContactSummary(contact, activities);
      setSummary(aiSummary);
    } catch (error) {
      console.error('Error generating summary:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRiskColor = (risk: ContactSummary['riskLevel']) => {
    switch (risk) {
      case 'low': return 'text-green-600 bg-green-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'high': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getRiskIcon = (risk: ContactSummary['riskLevel']) => {
    switch (risk) {
      case 'low': return CheckCircle;
      case 'medium': return AlertTriangle;
      case 'high': return AlertTriangle;
      default: return AlertTriangle;
    }
  };

  const getRiskLabel = (risk: ContactSummary['riskLevel']) => {
    switch (risk) {
      case 'low': return 'Bajo Riesgo';
      case 'medium': return 'Riesgo Medio';
      case 'high': return 'Alto Riesgo';
      default: return 'Sin Evaluar';
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mr-3"></div>
          <p className="text-gray-600">Generando resumen con IA...</p>
        </div>
      </div>
    );
  }

  if (!summary) {
    // Show contextual message based on contact status
    const getContextualMessage = () => {
      switch (contact.leadStatus) {
        case 'ya_es_cliente':
          return {
            title: 'Análisis de Cliente',
            description: 'Genera un análisis de retención y oportunidades de expansión para este cliente.',
            icon: '\ud83d\udc64'
          };
        case 'nuevo':
          return {
            title: 'Análisis de Lead Nuevo',
            description: 'Analiza el potencial de este lead y obtén sugerencias de calificación.',
            icon: '\u2728'
          };
        case 'en_seguimiento':
          return {
            title: 'Análisis de Seguimiento',
            description: 'Obtén insights sobre cómo avanzar este lead en el ciclo de ventas.',
            icon: '\ud83c\udfaf'
          };
        case 'cerrado':
          return {
            title: 'Análisis de Lead Cerrado',
            description: 'Revisa las razones del cierre y posibles oportunidades futuras.',
            icon: '\ud83d\udcc4'
          };
        case 'descartado':
          return {
            title: 'Contacto Descartado',
            description: 'Este contacto fue descartado. ¿Deseas analizar oportunidades de reactivación?',
            icon: '\u26a0\ufe0f'
          };
        default:
          return {
            title: 'Análisis de Contacto',
            description: 'Genera un análisis completo de este contacto.',
            icon: '\ud83e\udde0'
          };
      }
    };

    const contextInfo = getContextualMessage();

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="text-center py-8">
          <div className="text-5xl mb-4">{contextInfo.icon}</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{contextInfo.title}</h3>
          <p className="text-gray-500 mb-6 max-w-md mx-auto">{contextInfo.description}</p>
          <button
            onClick={generateSummary}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors shadow-md hover:shadow-lg flex items-center space-x-2 mx-auto"
          >
            <Brain className="w-5 h-5" />
            <span>Generar Análisis con IA</span>
          </button>
          <p className="text-xs text-gray-400 mt-3">Consumirá tokens de OpenAI</p>
        </div>
      </div>
    );
  }

  const RiskIcon = getRiskIcon(summary.riskLevel);

  return (
    <>
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Brain className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Resumen IA</h3>
              <p className="text-sm text-gray-500">
                Actualizado: {summary.lastUpdated.toLocaleString()}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className={`flex items-center space-x-2 px-3 py-1 rounded-full ${getRiskColor(summary.riskLevel)}`}>
              <RiskIcon className="w-4 h-4" />
              <span className="text-sm font-medium">{getRiskLabel(summary.riskLevel)}</span>
            </div>
            <button
              onClick={generateSummary}
              disabled={loading}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
              title="Actualizar resumen"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Summary Text */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-2">Resumen General</h4>
          <p className="text-gray-700 leading-relaxed">{summary.summary}</p>
        </div>

        {/* Key Points */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-3">Puntos Clave</h4>
          <div className="space-y-2">
            {summary.keyPoints.map((point, index) => (
              <div key={index} className="flex items-start space-x-2">
                <div className="w-1.5 h-1.5 bg-purple-600 rounded-full mt-2 flex-shrink-0"></div>
                <p className="text-sm text-gray-700">{point}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Next Steps */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-3">Próximos Pasos Sugeridos</h4>
          <div className="space-y-2">
            {summary.nextSteps.map((step, index) => (
              <div key={index} className="flex items-start space-x-2">
                <div className="w-5 h-5 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-medium mt-0.5 flex-shrink-0">
                  {index + 1}
                </div>
                <p className="text-sm text-gray-700">{step}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Risk Analysis */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-2">Análisis de Riesgo</h4>
          <div className="flex items-start space-x-2">
            <RiskIcon className={`w-5 h-5 mt-0.5 ${summary.riskLevel === 'low' ? 'text-green-600' : summary.riskLevel === 'medium' ? 'text-yellow-600' : 'text-red-600'}`} />
            <div className="flex-1">
              <span className="text-sm text-gray-700">
                {contact.leadStatus === 'ya_es_cliente' && summary.riskLevel === 'low' && 'Cliente estable con comunicación activa. Relación saludable.'}
                {contact.leadStatus === 'ya_es_cliente' && summary.riskLevel === 'medium' && 'Cliente requiere atención. Aumentar frecuencia de check-ins.'}
                {contact.leadStatus === 'ya_es_cliente' && summary.riskLevel === 'high' && 'ALERTA: Riesgo de churn. Contactar inmediatamente.'}

                {contact.leadStatus === 'en_seguimiento' && summary.riskLevel === 'low' && 'Lead activo con buen engagement. Continuar seguimiento.'}
                {contact.leadStatus === 'en_seguimiento' && summary.riskLevel === 'medium' && 'Lead tibio. Acelerar proceso o re-calificar.'}
                {contact.leadStatus === 'en_seguimiento' && summary.riskLevel === 'high' && 'Lead frío. Riesgo de pérdida. Reactivar urgentemente.'}

                {contact.leadStatus === 'nuevo' && summary.riskLevel === 'low' && 'Lead nuevo con contacto reciente. Actuar rápido.'}
                {contact.leadStatus === 'nuevo' && summary.riskLevel === 'medium' && 'Lead sin contacto inicial oportuno. Priorizar.'}
                {contact.leadStatus === 'nuevo' && summary.riskLevel === 'high' && 'Lead nuevo abandonado. Contactar urgentemente o descartar.'}

                {contact.leadStatus === 'cerrado' && 'Lead cerrado. Revisar razón y oportunidad futura.'}
                {contact.leadStatus === 'descartado' && 'Contacto descartado. Sin acciones requeridas.'}

                {!['ya_es_cliente', 'en_seguimiento', 'nuevo', 'cerrado', 'descartado'].includes(contact.leadStatus) && (
                  <>
                    {summary.riskLevel === 'low' && 'Contacto estable con seguimiento activo.'}
                    {summary.riskLevel === 'medium' && 'Requiere atención moderada.'}
                    {summary.riskLevel === 'high' && 'Acción inmediata requerida.'}
                  </>
                )}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default ContactSummaryAI;